import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { UpdateImmunizationBody } from '@/types/patient-chart-api';

/**
 * PUT /api/Patients/immunization/{id}
 *
 * Shared endpoint for Edit and soft-Delete on Immunizations. Wire body
 * curl-verified by Andrew 2026-05-20. See `UpdateImmunizationBody`
 * type doc for the round-trip rules (most notably: `performer` is the
 * ORIGINAL recording user — preserved, NOT overwritten with the
 * editing user; `performerDetails` round-trips full shape;
 * `createdDate` preserved from original record; `encounter`
 * round-tripped).
 *
 * Cache: invalidates `['patient-immunizations', patientId]`. SignalR
 * `UpdatePatientChat` "Vaccinations" event also fires for concurrent
 * sessions.
 */

interface UpdateImmunizationParams {
  patientId: string;
  immunizationId: string;
  body: UpdateImmunizationBody;
}

export function useUpdateImmunization() {
  const qc = useQueryClient();
  return useMutation<string, Error, UpdateImmunizationParams>({
    mutationFn: ({ immunizationId, body }) =>
      put<string>(`/api/Patients/immunization/${immunizationId}`, body),
    onSuccess: (_data, { patientId }) => {
      invalidateIfNotInFlight(qc, ['patient-immunizations', patientId]);
    },
  });
}
